import {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import classNames from "classnames"
import { EditVivintAdModal } from "components/EditVivintAdModal"
import UserService from "services/user.service"
import { formatDate, isPlainObject, objectToQueryString } from "utils/funcs"
import { useNotifications } from "hooks/useNotification"
import { useDebounce } from "hooks/useDebounce"
import { useRouter } from "hooks/useRouter"

const service = new UserService()

type VivintAdRow = {
  _id: string
  campaignName?: string
  full_name?: string
  first_name?: string
  last_name?: string
  state?: string
  city_name?: string
  position?: string
  ad_type?: string
  ad_status?: string
  ad_approval_status?: string
  season?: string
  payment?: string
  paymentOption?: string
  ad_amount?: string | number | null
  package_name?: string
  package_start_date?: string
  package_end_date?: string
  badge_id?: string
  phone_number?: string
  createdAt?: string
  isAiEnable?: string
  meet_link?: string | null
  notes?: string | null
}

type ListResponse = {
  success?: boolean
  data?: VivintAdRow[]
  total?: number
  totalDocument?: number
  totalPages?: number
  pageSize?: number
  currentPage?: number
  message?: string
}

/** Evo Media Ads (`MediaAds.tsx` ColumnFilters) — same classes */
const mediaAdsFieldClass =
  "w-full px-4 py-2 text-sm font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"

const td =
  "px-4 py-3 text-sm text-gray-700 whitespace-nowrap border-b border-gray-100"
const th =
  "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap bg-gray-50 border-b border-gray-200"

/** Matches Vivint all-ads API query params */
const INITIAL_MEDIA_FILTERS = {
  fullName: "",
  addStatus: "",
  paymentOption: "All",
  adApprovalStatus: "",
  season: "All",
  adType: "All",
  isAiEnable: "",
}

function generatePageSizeOptions(max = 1000) {
  const out: number[] = []
  for (let i = 10; i <= max; i += 10) out.push(i)
  return out
}

export const VivintAdPricing = () => {
  const { push } = useRouter()
  const { errorMessage, successMessage } = useNotifications()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<VivintAdRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [pageState, setPageState] = useState({
    pageNumber: 1,
    pageSize: 100,
  })

  const [filters, setFilters] = useState({ ...INITIAL_MEDIA_FILTERS })
  const debouncedFilters = useDebounce(filters, 500)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedAd, setSelectedAd] = useState<Record<string, unknown>>({})
  const [exporting, setExporting] = useState(false)

  const applyFilterParams = useCallback(
    (q: Record<string, string | undefined>, f: typeof INITIAL_MEDIA_FILTERS) => {
      if (f.fullName.trim()) q.fullName = f.fullName.trim()
      if (f.addStatus) q.addStatus = f.addStatus
      if (f.paymentOption && f.paymentOption !== "All")
        q.paymentOption = f.paymentOption
      if (f.adApprovalStatus) q.adApprovalStatus = f.adApprovalStatus
      if (f.season && f.season !== "All") q.season = f.season
      if (f.adType && f.adType !== "All") q.adType = f.adType
      if (f.isAiEnable) q.isAiEnable = f.isAiEnable
    },
    []
  )

  const buildQuery = useCallback(() => {
    const q: Record<string, string | undefined> = {
      currentPage: String(pageState.pageNumber),
      pageSize: String(pageState.pageSize),
    }
    applyFilterParams(q, debouncedFilters)
    return q
  }, [pageState.pageNumber, pageState.pageSize, debouncedFilters, applyFilterParams])

  const buildExportQuery = useCallback(() => {
    const q: Record<string, string | undefined> = {}
    applyFilterParams(q, debouncedFilters)
    return q
  }, [debouncedFilters, applyFilterParams])

  const fetchData = useCallback(() => {
    setLoading(true)
    const q = buildQuery()
    service
      .getVivintAdsDashboardAllAds(q)
      .then((resp) => {
        const body = resp.data as ListResponse
        const list = Array.isArray(body?.data) ? body.data : []
        setRows(list)
        const t =
          typeof body?.total === "number"
            ? body.total
            : typeof body?.totalDocument === "number"
              ? body.totalDocument
              : list.length
        setTotal(t)
        const tp =
          typeof body?.totalPages === "number" && body.totalPages >= 1
            ? body.totalPages
            : Math.max(
                1,
                Math.ceil(t / pageState.pageSize) || 1
              )
        setTotalPages(tp)
      })
      .catch(() => {
        errorMessage("Could not load ads")
      })
      .finally(() => setLoading(false))
  }, [buildQuery, errorMessage, pageState.pageSize])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const hasFilters = useMemo(() => {
    const f = filters
    return (
      f.fullName.trim() !== "" ||
      f.addStatus !== "" ||
      (f.paymentOption !== "" && f.paymentOption !== "All") ||
      f.adApprovalStatus !== "" ||
      (f.season !== "" && f.season !== "All") ||
      (f.adType !== "" && f.adType !== "All") ||
      f.isAiEnable !== ""
    )
  }, [filters])

  const handleExportCsv = useCallback(async () => {
    setExporting(true)
    try {
      const resp = await service.downloadVivintAdsDashboardCsv(
        buildExportQuery()
      )
      const body = resp.data as unknown
      let csvText: string | null = null
      if (typeof body === "string" && body.trim().length > 0) {
        csvText = body
      } else if (isPlainObject(body)) {
        const wrapped = (body as { data?: unknown }).data
        if (typeof wrapped === "string" && wrapped.trim().length > 0) {
          csvText = wrapped
        }
      }
      if (csvText) {
        const blob = new Blob(["\uFEFF" + csvText], {
          type: "text/csv;charset=utf-8;",
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", "vivint-ads-export.csv")
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        successMessage("CSV downloaded")
        return
      }
      errorMessage("No export data returned")
    } catch {
      errorMessage("Export failed")
    } finally {
      setExporting(false)
    }
  }, [buildExportQuery, errorMessage, successMessage])

  const openEditAd = (row: VivintAdRow) => {
    setSelectedAd({
      ...row,
      id: row._id,
      meet_link: row.meet_link ?? "",
      city_name: row.city_name ?? "",
    })
    setEditModalOpen(true)
  }

  const handleFilterChange = (
    e: ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
    setPageState((p) => ({ ...p, pageNumber: 1 }))
  }

  const clearFilter = () => {
    setFilters({ ...INITIAL_MEDIA_FILTERS })
    setPageState((p) => ({ ...p, pageNumber: 1, pageSize: 100 }))
  }

  const startRow =
    total === 0 ? 0 : (pageState.pageNumber - 1) * pageState.pageSize + 1
  const endRow = Math.min(pageState.pageNumber * pageState.pageSize, total)

  const goPrev = () =>
    setPageState((p) => ({
      ...p,
      pageNumber: Math.max(1, p.pageNumber - 1),
    }))
  const goNext = () =>
    setPageState((p) => ({
      ...p,
      pageNumber: Math.min(totalPages || 1, p.pageNumber + 1),
    }))

  const cell = (v: unknown) =>
    v == null || v === "" ? "—" : String(v)

  const queryPreview = useMemo(
    () => objectToQueryString(buildQuery()),
    [buildQuery]
  )

  return (
    <Fragment>
      {/* Match Evo Media Ads page title */}
      <div className="flex flex-col mb-4">
        <h1 className="page-title">
          Vivint Ads
        </h1>
        <p className="page-subtitle">View all Vivint Ads</p>
      </div>

      <div className="relative isolate overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card">
        {/* Toolbar — same structure as `common/Table/Table` + Media Ads `filters` slot */}
        <div className="border-b border-gray-200 bg-white px-4 pt-4 pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-500 whitespace-nowrap">
                Rows:
              </label>
              <select
                value={pageState.pageSize}
                onChange={(e) => {
                  setPageState({
                    pageNumber: 1,
                    pageSize: +e.target.value,
                  })
                }}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary/20 focus:outline-none transition-colors"
              >
                {generatePageSizeOptions(1000).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            {hasFilters ? (
              <button
                type="button"
                onClick={clearFilter}
                className="px-3 py-2 text-sm text-white bg-ink hover:bg-gray-800 rounded-lg whitespace-nowrap"
              >
                Clear Filters
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleExportCsv}
              disabled={exporting}
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
                exporting
                  ? "cursor-not-allowed bg-gray-200 text-gray-500"
                  : "bg-primary text-white hover:bg-primary-600"
              )}
            >
              {exporting ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Exporting…
                </>
              ) : (
                "Export CSV"
              )}
            </button>
          </div>

          {/* API params: fullName, addStatus, paymentOption, adApprovalStatus, season, adType, isAiEnable (+ isDownload on export) */}
          <div className="mt-2">
            <div className="z-50 flex flex-wrap items-start gap-3 mt-4">
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  Name
                </label>
                <input
                  className={mediaAdsFieldClass}
                  type="text"
                  name="fullName"
                  placeholder="Search by Name"
                  value={filters.fullName}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  Ad Status
                </label>
                <select
                  name="addStatus"
                  value={filters.addStatus}
                  className={mediaAdsFieldClass}
                  onChange={handleFilterChange}
                >
                  <option value="">Select status</option>
                  <option value="In Process">In Process</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  Payment option
                </label>
                <select
                  name="paymentOption"
                  value={filters.paymentOption}
                  className={mediaAdsFieldClass}
                  onChange={handleFilterChange}
                >
                  <option value="All">All</option>
                  <option value="Frontend">Frontend</option>
                  <option value="Backend">Backend</option>
                </select>
              </div>
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  Approval Status
                </label>
                <select
                  className={mediaAdsFieldClass}
                  name="adApprovalStatus"
                  value={filters.adApprovalStatus}
                  onChange={handleFilterChange}
                >
                  <option value="">Select status</option>
                  <option value="UNDER REVIEW">UNDER REVIEW</option>
                  <option value="DENIED">DENIED</option>
                  <option value="APPROVED">APPROVED</option>
                </select>
              </div>
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  Season
                </label>
                <select
                  className={mediaAdsFieldClass}
                  name="season"
                  value={filters.season}
                  onChange={handleFilterChange}
                >
                  <option value="All">All</option>
                  <option value="Summer">Summer</option>
                  <option value="Year Round">Year Round</option>
                  <option value="Summer & Year Round">Summer & Year Round</option>
                </select>
              </div>
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  Ad Types
                </label>
                <select
                  className={mediaAdsFieldClass}
                  name="adType"
                  value={filters.adType}
                  onChange={handleFilterChange}
                >
                  <option value="All">All</option>
                  <option value="RECRUITING">RECRUITING</option>
                  <option value="SALES">SALES</option>
                </select>
              </div>
              <div className="flex flex-col flex-1 gap-1 min-w-[160px] basis-[160px]">
                <label className="field-label whitespace-nowrap">
                  AI Enabled
                </label>
                <select
                  className={mediaAdsFieldClass}
                  name="isAiEnable"
                  value={filters.isAiEnable}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  <option value="Screening">Screening</option>
                  <option value="Scheduling">Scheduling</option>
                </select>
              </div>
            </div>
          </div>

          <details className="mt-3 rounded-md border border-dashed border-gray-200 bg-gray-50/80 px-3 py-2 text-xs text-gray-500">
            <summary className="cursor-pointer select-none font-medium text-gray-600 hover:text-gray-800">
              Request query string
            </summary>
            <code className="mt-2 block break-all text-[11px] text-gray-600">
              {queryPreview || "(none)"}
            </code>
          </details>
        </div>

        {loading && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-[1px]"
            aria-busy
          >
            <svg
              className="h-8 w-8 animate-spin text-gray-200 fill-primary"
              viewBox="0 0 100 101"
              aria-hidden
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <p className="mt-2 text-xs font-medium text-gray-500">
              Loading ads…
            </p>
          </div>
        )}

        <div className="max-h-[min(70vh,720px)] overflow-x-auto overflow-y-auto">
          <table className="min-w-full text-left">
            <thead className="sticky top-0 z-[1] shadow-sm">
              <tr>
                <th className={th}>#</th>
                <th className={th}>Campaign</th>
                <th className={th}>Rep Name</th>
                <th className={th}>Badge ID</th>
                <th className={th}>Location</th>
                <th className={th}>Position</th>
                <th className={th}>Type</th>
                <th className={th}>Ad status</th>
                <th className={th}>Approval</th>
                <th className={th}>Season</th>
                <th className={th}>Payment</th>
                <th className={th}>Option</th>
                <th className={th}>Amount</th>
                <th className={th}>Package</th>
                <th className={th}>Run dates</th>
                <th className={th}>Phone</th>
                <th className={th}>Created</th>
                <th
                  className={classNames(
                    th,
                    "sticky right-0 z-[2] bg-gray-50 shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)]"
                  )}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={18} className="px-4 py-16 text-center">
                    <p className="text-sm font-medium text-gray-500">
                      No ads match these filters
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      Try clearing filters or changing status selections.
                    </p>
                  </td>
                </tr>
              )}
              {rows.map((row, index) => (
                <tr
                  key={row._id}
                  className={classNames(
                    "transition-colors hover:bg-primary-50/40",
                    {
                      "bg-gray-50/80": index % 2 !== 0,
                      "bg-white": index % 2 === 0,
                    }
                  )}
                >
                  <td className={classNames(td, "text-gray-400 tabular-nums")}>
                    {startRow + index}
                  </td>
                  <td className={td}>{cell(row.campaignName)}</td>
                  <td className={td}>{cell(row.full_name)}</td>
                  <td className={td}>{cell(row.badge_id)}</td>
                  <td className={td}>
                    {[row.city_name, row.state].filter(Boolean).join(", ") ||
                      "—"}
                  </td>
                  <td className={td}>{cell(row.position)}</td>
                  <td className={td}>{cell(row.ad_type)}</td>
                  <td className={td}>{cell(row.ad_status)}</td>
                  <td className={td}>{cell(row.ad_approval_status)}</td>
                  <td className={td}>{cell(row.season)}</td>
                  <td className={td}>{cell(row.payment)}</td>
                  <td className={td}>{cell(row.paymentOption)}</td>
                  <td className={td}>{cell(row.ad_amount)}</td>
                  <td className={td}>{cell(row.package_name)}</td>
                  <td className={td}>
                    {row.package_start_date || row.package_end_date
                      ? `${formatDate(row.package_start_date)} – ${formatDate(row.package_end_date)}`
                      : "—"}
                  </td>
                  <td className={td}>{cell(row.phone_number)}</td>
                  <td className={td}>{formatDate(row.createdAt)}</td>
                  <td
                    className={classNames(
                      td,
                      "sticky right-0 z-[1] bg-white shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)]",
                      index % 2 !== 0 ? "bg-gray-50/80" : "bg-white"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title="View details"
                        onClick={() => push(`/vivint-ads/ad/${row._id}`)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-6 w-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title="Edit ad"
                        onClick={() => openEditAd(row)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-6 w-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/50 px-4 py-3">
          <p className="text-xs text-gray-600">
            {total === 0 ? (
              <span className="text-gray-500">0 results</span>
            ) : (
              <>
                <span className="font-medium text-gray-800">
                  Showing {startRow}–{endRow}
                </span>
                <span className="text-gray-500"> of {total}</span>
                {totalPages > 1 && (
                  <span className="text-gray-400">
                    {" "}
                    · Page {pageState.pageNumber} of {totalPages}
                  </span>
                )}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={loading || pageState.pageNumber <= 1}
              className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={
                loading ||
                pageState.pageNumber >= totalPages ||
                totalPages <= 1 ||
                rows.length === 0
              }
              className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <EditVivintAdModal
        showModal={editModalOpen}
        setShowModal={setEditModalOpen}
        selected={selectedAd}
        setSelected={setSelectedAd}
        fetchData={fetchData}
      />
    </Fragment>
  )
}
